/**
 * Endoscopy SOP-class routing.
 *
 * EndoEdge bundles a procedure as one DICOM Study with two Series:
 *   - Series N (SeriesNumber=1) "Endoscopy Snapshots" — VL Endoscopic
 *     Image Storage SOP Instances, JPEG Baseline transfer syntax.
 *   - Series M (SeriesNumber=2) "Endoscopy Video" — Video Endoscopic
 *     Image Storage SOP Instances, MPEG-4 AVC transfer syntax.
 *
 * The viewer keys every dual-panel decision off the SOP Class UID
 * carried in QIDO instance row tag `(0008,0016)`.
 */
import type { StudyInstance } from "@viewer/api/client";

export const SOP_VL_ENDOSCOPIC_IMAGE    = "1.2.840.10008.5.1.4.1.1.77.1.1";
export const SOP_VIDEO_ENDOSCOPIC_IMAGE = "1.2.840.10008.5.1.4.1.1.77.1.1.1";

export type SeriesKind = "stills" | "video" | "other";

/** Classify a single instance by its SOP class UID. */
export function instanceKind(inst: StudyInstance | undefined | null): SeriesKind {
  if (!inst) return "other";
  switch (inst.sopClassUid) {
    case SOP_VL_ENDOSCOPIC_IMAGE:    return "stills";
    case SOP_VIDEO_ENDOSCOPIC_IMAGE: return "video";
    default:                         return "other";
  }
}

/** Classify a series by examining its instances. A series that mixes
 *  SOP classes is reported by its first non-`other` instance — DICOM
 *  PS3.3 Series IE allows mixed SOP classes within a series, but in
 *  the endoscopy bundle we expect homogeneous series (vendors split
 *  on this and we follow Karl Storz AIDA's one-series-per-SOP-class
 *  convention). */
export function seriesKind(instances: StudyInstance[]): SeriesKind {
  for (const i of instances) {
    const k = instanceKind(i);
    if (k !== "other") return k;
  }
  // Heuristic fallback for legacy imports that lost the SOP class —
  // SeriesDescription is operator-controlled but endopod sender uses
  // the canonical "Endoscopy Snapshots" / "Endoscopy Video" labels.
  const desc = (instances[0]?.seriesDescription || "").toLowerCase();
  if (desc.includes("snapshot") || desc.includes("still") || desc.includes("image")) return "stills";
  if (desc.includes("video")    || desc.includes("clip")  || desc.includes("movie")) return "video";
  return "other";
}

export interface SeriesGroup {
  seriesUid:    string;
  seriesNumber: number;
  description:  string;
  modality:     string;
  kind:         SeriesKind;
  instances:    StudyInstance[];
}

/** Group every instance in a study by Series UID, classify each
 *  series, and sort by SeriesNumber. */
export function groupByseries(all: StudyInstance[]): SeriesGroup[] {
  const map = new Map<string, StudyInstance[]>();
  for (const i of all) {
    const arr = map.get(i.seriesUid) ?? [];
    arr.push(i);
    map.set(i.seriesUid, arr);
  }
  const out: SeriesGroup[] = [];
  for (const [uid, list] of map) {
    list.sort((a, b) => a.instanceNumber - b.instanceNumber);
    out.push({
      seriesUid:    uid,
      seriesNumber: list[0].seriesNumber ?? 0,
      description:  list[0].seriesDescription ?? "",
      modality:     list[0].modality ?? "",
      kind:         seriesKind(list),
      instances:    list,
    });
  }
  out.sort((a, b) => a.seriesNumber - b.seriesNumber);
  return out;
}
